import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { CardSkeleton } from '../../Components/common/Skeleton';
import { getAvatarUrl } from '../../utils/avatar';
import PasswordStrengthGuide, { getPasswordValidation } from '../../Components/common/PasswordStrengthGuide';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  ShieldCheck,
  Clock,
  Briefcase,
  FileText,
  ExternalLink,
  ArrowLeft,
  Edit,
  Trash2,
  CheckCircle2,
  XCircle,
  Building2,
  Medal,
  Activity,
  Link as LinkIcon,
  Copy,
  Check,
  Download
} from 'lucide-react';

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [copied, setCopied] = useState(false);


  useEffect(() => {
    setAvatarError(false);
  }, [user?.avatar]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    isActive: true,
    doctorCategory: '',
    street: '',
    city: '',
    state: '',
    pincode: '',
    aadhaarCard: null,
    panCard: null,
    avatar: null
  });

  const [availableRoles, setAvailableRoles] = useState([]);

  useEffect(() => {
    fetchUser();
    fetchRoles();
  }, [id]);

  const fetchRoles = async () => {
    try {
      const res = await api.get('/roles');
      if (res.data?.data) {
        setAvailableRoles(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load roles list:', err);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await api.get(`/users/${id}`);
      setUser(response.data.data);
      setFormData({
        firstName: response.data.data.firstName,
        lastName: response.data.data.lastName,
        email: response.data.data.email,
        phone: response.data.data.phone,
        role: response.data.data.role,
        isActive: response.data.data.isActive,
        doctorCategory: response.data.data.doctorCategory || '',
        street: response.data.data.address?.street || '',
        city: response.data.data.address?.city || '',
        state: response.data.data.address?.state || '',
        pincode: response.data.data.address?.pincode || '',
        aadhaarCard: null,
        panCard: null,
        avatar: null
      });
      // Fetch referral data after user loads
      fetchReferral();
    } catch (error) {
      toast.error('Failed to load user');
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferral = async () => {
    setReferralLoading(true);
    try {
      const res = await api.get(`/users/${id}/referral`);
      setReferralData(res.data.data);
    } catch (err) {
      console.error('Referral fetch error:', err);
    } finally {
      setReferralLoading(false);
    }
  };

  const handleCopyReferralLink = () => {
    if (!referralData?.referralUrl) return;
    navigator.clipboard.writeText(referralData.referralUrl)
      .then(() => {
        setCopied(true);
        toast.success('Referral link copied!');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => toast.error('Failed to copy'));
  };

  const handleDownloadQRCode = () => {
    if (!referralData?.qrCodeDataUrl) return;
    const link = document.createElement('a');
    link.href = referralData.qrCodeDataUrl;
    link.download = `referral-qrcode-${referralData.referralCode || 'code'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code download started!');
  };

  const handleUpdate = async () => {
    setUpdating(true);
    const body = new FormData();
    body.append('firstName', formData.firstName);
    body.append('lastName', formData.lastName);
    body.append('email', formData.email);
    body.append('phone', formData.phone);
    body.append('role', formData.role);
    body.append('isActive', formData.isActive);
    body.append('doctorCategory', formData.doctorCategory);
    
    // address
    body.append('address[street]', formData.street);
    body.append('address[city]', formData.city);
    body.append('address[state]', formData.state);
    body.append('address[pincode]', formData.pincode);
    body.append('address[country]', 'India');

    // docs
    if (formData.avatar) body.append('avatar', formData.avatar);
    if (formData.aadhaarCard) body.append('aadhaarCard', formData.aadhaarCard);
    if (formData.panCard) body.append('panCard', formData.panCard);

    try {
      await api.put(`/users/${id}`, body, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('User updated successfully!');
      setShowEditModal(false);
      fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update user');
    } finally {
      setUpdating(false);
    }
  };

  const handleResetPassword = async () => {
    const validation = getPasswordValidation(newPassword);
    if (!validation.isValid) {
      toast.error(validation.firstError || 'Password must meet security requirements');
      return;
    }
    setUpdating(true);
    try {
      await api.post(`/users/${id}/reset-password`, { newPassword });
      toast.success('Password reset successfully!');
      setShowResetModal(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    setUpdating(true);
    try {
      await api.delete(`/users/${id}`);
      toast.success('User deleted successfully!');
      navigate('/users');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setUpdating(false);
    }
  };

  const handleResendMoU = async () => {
    setUpdating(true);
    try {
      const response = await api.post(`/users/${id}/resend-mou`);
      toast.success(response.data?.message || 'MoU PDF resent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend MoU PDF');
    } finally {
      setUpdating(false);
    }
  };

  const handleTriggerResignMoU = async () => {
    if (!window.confirm('Are you sure you want to request this user to re-sign their MoU? This will generate a fresh signature link.')) {
      return;
    }
    setUpdating(true);
    try {
      const response = await api.post(`/users/${id}/trigger-resign-mou`);
      toast.success(response.data?.message || 'MoU re-signature requested successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to request MoU re-signature');
    } finally {
      setUpdating(false);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      customer_care: 'bg-blue-100 text-blue-800',
      cc_head: 'bg-indigo-100 text-indigo-800',
      doctor: 'bg-green-100 text-green-800',
      hospital: 'bg-purple-100 text-purple-800',
      marketing_agent: 'bg-yellow-100 text-yellow-800',
      accountant: 'bg-orange-100 text-orange-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="h-10 w-10 bg-slate-200 animate-pulse rounded-lg"></div>
            <div className="h-8 w-48 bg-slate-200 animate-pulse rounded-lg"></div>
          </div>
          <div className="h-10 w-32 bg-slate-100 rounded-lg animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm space-y-6">
              <div className="h-6 w-1/3 bg-slate-100 rounded animate-pulse"></div>
              <div className="grid grid-cols-2 gap-8">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="space-y-2">
                    <div className="h-3 w-20 bg-slate-50 rounded animate-pulse"></div>
                    <div className="h-5 w-full bg-slate-100 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <CardSkeleton />
            <CardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">User not found</p>
        <Link to="/users" className="text-primary-600 hover:text-primary-700">
          Back to users
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/users')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            ←
          </button>
          <h1 className="text-2xl font-bold text-gray-900">User Details</h1>
        </div>
        <div className="flex space-x-2">
          {(currentUser?.role === 'admin' || currentUser?.role === 'customer_care' || currentUser?.role === 'cc_head') && (
            <button
              onClick={() => setShowResetModal(true)}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-bold"
            >
              Reset Password
            </button>
          )}
          {(currentUser?.role === 'admin' || currentUser?.role === 'cc_head') && (
            <button
              onClick={() => setShowEditModal(true)}
              className="px-4 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 font-bold"
            >
              Edit
            </button>
          )}
          {currentUser?.role === 'admin' && (
            <>
              {['doctor', 'hospital', 'marketing_agent'].includes(user.role) && (
                <>
                  <button
                    onClick={handleResendMoU}
                    disabled={updating}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-bold shadow-sm"
                  >
                    Resend Signed MoU
                  </button>
                  <button
                    onClick={handleTriggerResignMoU}
                    disabled={updating}
                    className="px-4 py-2 border border-amber-600 text-amber-600 rounded-lg hover:bg-amber-50 disabled:opacity-50 font-bold"
                  >
                    Request Re-sign
                  </button>
                </>
              )}
              <button
                onClick={handleDelete}
                disabled={updating}
                className="px-4 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 font-bold"
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8 overflow-hidden relative">
            <div className="absolute top-0 right-0 p-8 opacity-5">
              <User className="w-32 h-32" />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-8">
              <div className="w-24 h-24 rounded-3xl bg-slate-100 border-4 border-white shadow-xl overflow-hidden flex-shrink-0">
                {user.avatar && !avatarError ? (
                  <img
                    src={getAvatarUrl(user.avatar)}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-blue-50 text-blue-500 font-bold text-xl uppercase">
                    {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                  </div>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-black text-slate-900 flex items-center gap-3">
                  {user.firstName} {user.lastName}
                </h2>
                <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mt-1">
                  {user.role === 'marketing_agent' ? 'Lead Partner' : user.role === 'cc_head' ? 'Customer Care Head' : user.role.replace('_', ' ')} Profile
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 relative z-10">
              <div className="space-y-1">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Full Identity</p>
                <p className="text-lg font-bold text-slate-700">{user.firstName} {user.lastName}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Digital Address</p>
                <p className="text-lg font-bold text-slate-700">{user.email}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Contact Line</p>
                <p className="text-lg font-bold text-slate-700">{user.phone}</p>
              </div>
              {user.alternativePhone && (
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Backup Contact</p>
                  <p className="text-lg font-bold text-slate-700">{user.alternativePhone}</p>
                </div>
              )}
              {user.customId && (
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Doxez ID</p>
                  <p className="text-lg font-bold text-slate-700">{user.customId}</p>
                </div>
              )}
              {user.referralCode && (
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Referral Code</p>
                  <p className="text-lg font-bold text-slate-700">{user.referralCode}</p>
                </div>
              )}
              {user.role !== 'hospital' && (
                <>
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Date of Birth</p>
                    <p className="text-lg font-bold text-slate-700">
                      {user.dob ? new Date(user.dob).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : (user.onboardingData?.dob || 'Not Specified')}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Gender Profile</p>
                    <p className="text-lg font-bold text-slate-700">{user.gender || user.onboardingData?.gender || 'Not Specified'}</p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
            <h2 className="text-xl font-black text-slate-900 mb-8 flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600">
                <MapPin className="w-5 h-5" />
              </div>
              Geographic Profile
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div className="sm:col-span-2 space-y-1">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Street Address</p>
                <p className="text-lg font-bold text-slate-700">{user.address?.street || user.onboardingData?.currentAddress || 'No street details provided'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">City</p>
                <p className="text-lg font-bold text-slate-700">{user.address?.city || user.onboardingData?.city || 'N/A'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">State / Region</p>
                <p className="text-lg font-bold text-slate-700">{user.address?.state || user.onboardingData?.state || 'N/A'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Postal Code</p>
                <p className="text-lg font-bold text-slate-700">{user.address?.pincode || user.onboardingData?.pincode || 'N/A'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Country</p>
                <p className="text-lg font-bold text-slate-700">{user.address?.country || 'India'}</p>
              </div>
            </div>
          </div>

          {/* Role Specific Details */}
          {user.role === 'doctor' && user.doctorProfile && (
            <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
              <h2 className="text-xl font-black text-slate-900 mb-8 flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600">
                  <Medal className="w-5 h-5" />
                </div>
                Professional Doctor Profile
              </h2>
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Specialization (Degree)</p>
                  <p className="text-lg font-bold text-slate-700">
                    {user.doctorProfile.specializationBranch
                      ? `${user.doctorProfile.specializationBranch} (${user.doctorProfile.specialization})`
                      : user.doctorProfile.specialization || 'N/A'}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Qualification</p>
                  <p className="text-lg font-bold text-slate-700">{user.doctorProfile.primaryQualification || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Year of Completion</p>
                  <p className="text-lg font-bold text-slate-700">{user.doctorProfile.yearOfCompletion || user.onboardingData?.yearOfCompletion || 'N/A'}</p>
                </div>
                {(user.doctorProfile.specializationYear || user.onboardingData?.specializationYear) && (
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Specialization Year</p>
                    <p className="text-lg font-bold text-slate-700">{user.doctorProfile.specializationYear || user.onboardingData?.specializationYear}</p>
                  </div>
                )}
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Experience (Years)</p>
                  <p className="text-lg font-bold text-slate-700">{user.doctorProfile.totalExperience || user.onboardingData?.totalExperience || '0'} Years</p>
                </div>
                {(user.doctorProfile.postSpecializationExperience || user.onboardingData?.postSpecializationExperience) ? (
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Post-Specialization Exp.</p>
                    <p className="text-lg font-bold text-slate-700">{user.doctorProfile.postSpecializationExperience || user.onboardingData?.postSpecializationExperience} Years</p>
                  </div>
                ) : null}
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Registration No</p>
                  <p className="text-lg font-bold text-slate-700">{user.doctorProfile.registrationNumber || user.onboardingData?.registrationNumber || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Council Name</p>
                  <p className="text-lg font-bold text-slate-700">{user.doctorProfile.councilName || user.onboardingData?.councilName || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Working Hospital</p>
                  <p className="text-lg font-bold text-slate-700">{user.doctorProfile.workingHospital || user.onboardingData?.workingHospital || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Indemnity Insurance</p>
                  <p className="text-lg font-bold text-slate-700">{(user.doctorProfile.insurance?.hasInsurance || user.onboardingData?.insurance?.hasInsurance) ? 'Yes' : 'No'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Verification Status</p>
                  <span className={`inline-block px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-full ${user.doctorProfile.verificationStatus === 'verified' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                    {user.doctorProfile.verificationStatus || 'Pending'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Bank & Financials Profile */}
          {(user.onboardingData?.bankName || user.onboardingData?.accountNumber || user.onboardingData?.bankDetails?.bankName || user.onboardingData?.financials?.bankName) && (
            <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
              <h2 className="text-xl font-black text-slate-900 mb-8 flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600">
                  <Activity className="w-5 h-5" />
                </div>
                Financial & Settlement Profile
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Bank Name</p>
                  <p className="text-lg font-bold text-slate-700">{user.onboardingData?.bankName || user.onboardingData?.bankDetails?.bankName || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Account Number</p>
                  <p className="text-lg font-bold text-slate-700">{user.onboardingData?.accountNumber || user.onboardingData?.bankDetails?.accountNumber || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">IFSC Code</p>
                  <p className="text-lg font-bold text-slate-700">{user.onboardingData?.ifscCode || user.onboardingData?.bankDetails?.ifscCode || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">PAN Identification</p>
                  <p className="text-lg font-bold text-slate-700">{user.onboardingData?.panNumber || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {user.role === 'hospital' && user.hospitalProfile && (
            <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
              <h2 className="text-xl font-black text-slate-900 mb-8 flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600">
                  <Building2 className="w-5 h-5" />
                </div>
                Hospital Entity Details
              </h2>
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Hospital Name</p>
                  <p className="text-lg font-bold text-slate-700">{user.hospitalProfile.hospitalName || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Registration No</p>
                  <p className="text-lg font-bold text-slate-700">{user.hospitalProfile.registrationNumber || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Hospital Type</p>
                  <p className="text-lg font-bold text-slate-700">{user.hospitalProfile.hospitalType || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Verification</p>
                  <span className={`inline-block px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-full ${user.hospitalProfile.verificationStatus === 'verified' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                    {user.hospitalProfile.verificationStatus || 'Pending'}
                  </span>
                </div>
              </div>
            </div>
          )}


          {/* Identity & Onboarding Documents */}
          <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
            <h2 className="text-xl font-black text-slate-900 mb-8 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center text-amber-600">
                <FileText className="w-5 h-5" />
              </div>
              Identity & Onboarding Documents
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Aadhaar Card */}
              {user.aadhaarCard?.url && (
                <div className="p-4 bg-blue-50/30 rounded-2xl border border-blue-100 flex items-center justify-between hover:border-blue-200 transition-all group overflow-hidden">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 bg-white rounded-xl flex-shrink-0 flex items-center justify-center text-blue-500 shadow-sm border border-slate-50">
                      <ShieldCheck className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-700 truncate">Aadhaar Card</p>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest truncate">Verified Identity</p>
                    </div>
                  </div>
                  <a href={`/api/${user.aadhaarCard.url.startsWith('/') ? user.aadhaarCard.url.substring(1) : user.aadhaarCard.url}`} target="_blank" rel="noopener noreferrer" className="ml-3 p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all flex-shrink-0">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              )}

              {/* PAN Card */}
              {user.panCard?.url && (
                <div className="p-4 bg-orange-50/30 rounded-2xl border border-orange-100 flex items-center justify-between hover:border-orange-200 transition-all group overflow-hidden">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 bg-white rounded-xl flex-shrink-0 flex items-center justify-center text-orange-500 shadow-sm border border-slate-50">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-700 truncate">PAN Card</p>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest truncate">Tax Identity</p>
                    </div>
                  </div>
                  <a href={`/api/${user.panCard.url.startsWith('/') ? user.panCard.url.substring(1) : user.panCard.url}`} target="_blank" rel="noopener noreferrer" className="ml-3 p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all flex-shrink-0">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              )}

              {/* Other Onboarding Documents */}
              {user.onboardingDocuments && user.onboardingDocuments.length > 0 ? (
                user.onboardingDocuments.map((doc, idx) => (
                  <div key={idx} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between hover:border-blue-200 transition-all group overflow-hidden">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 bg-white rounded-xl flex-shrink-0 flex items-center justify-center text-slate-400 shadow-sm border border-slate-50 group-hover:text-blue-500">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-700 truncate" title={doc.name}>
                          {doc.name || 'Document'}
                        </p>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest truncate">{doc.type || 'Standard'}</p>
                      </div>
                    </div>
                    <a href={doc.url} target="_blank" rel="noopener noreferrer" className="ml-3 p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all flex-shrink-0">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                ))
              ) : (
                (!user.aadhaarCard?.url && !user.panCard?.url) && (
                  <div className="sm:col-span-2 text-center py-12 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 font-bold">No documents submitted yet</p>
                    <p className="text-xs text-slate-400 uppercase tracking-widest mt-1">Verification pending documents</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Role & Status</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Role</p>
                <span className={`px-2 py-1 text-xs rounded-full ${getRoleColor(user.role)}`}>
                  {user.role === 'marketing_agent' ? 'Lead Partner' : user.role === 'cc_head' ? 'Customer Care Head' : user.role.replace('_', ' ')}
                </span>
              </div>
              {user.role === 'doctor' && user.doctorCategory && (
                <div>
                  <p className="text-sm text-gray-600">Doctor Type</p>
                  <span className={`px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-800`}>
                    {user.doctorCategory === 'inhouse' ? 'In-House' : 'External'}
                  </span>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                >
                  {user.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Verified</p>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${user.isVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}
                >
                  {user.isVerified ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
            <h2 className="text-xl font-black text-slate-900 mb-8 flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center text-orange-600">
                <Clock className="w-5 h-5" />
              </div>
              System Timeline
            </h2>
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Account Created</p>
                  <p className="text-sm font-bold text-slate-700">{new Date(user.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                </div>
                <Clock className="w-4 h-4 text-slate-300" />
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Last Update</p>
                  <p className="text-sm font-bold text-slate-700">{new Date(user.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                </div>
                <Activity className="w-4 h-4 text-slate-300" />
              </div>
              {user.lastLogin && (
                <div className="flex items-center justify-between p-4 bg-blue-50/50 rounded-2xl border border-blue-100/50">
                  <div>
                    <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Last Activity</p>
                    <p className="text-sm font-bold text-blue-700">{new Date(user.lastLogin).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                </div>
              )}
            </div>
          </div>

          {/* ─── Referral Card ─── */}
          <div className="bg-gradient-to-br from-[#143E78] to-[#1e5faa] rounded-3xl shadow-2xl shadow-blue-900/20 p-6 text-white overflow-hidden relative">
            <div className="absolute -top-6 -right-6 w-28 h-28 bg-white/5 rounded-full blur-2xl" />
            <h2 className="text-xs font-black text-white/80 uppercase tracking-widest mb-4 flex items-center gap-2 relative z-10">
              <div className="w-7 h-7 bg-white/10 rounded-lg flex items-center justify-center">
                <LinkIcon className="w-3.5 h-3.5" />
              </div>
              Referral Link
            </h2>

            {referralLoading ? (
              <div className="space-y-3 animate-pulse relative z-10">
                <div className="h-28 w-28 mx-auto bg-white/10 rounded-2xl" />
                <div className="h-4 bg-white/10 rounded-lg" />
                <div className="h-8 bg-white/10 rounded-xl" />
              </div>
            ) : referralData ? (
              <div className="space-y-3 relative z-10">
                {/* QR Code */}
                <div className="flex justify-center">
                  <div className="bg-white p-2.5 rounded-2xl shadow-xl">
                    <img src={referralData.qrCodeDataUrl} alt="QR" className="w-24 h-24 rounded-lg" />
                  </div>
                </div>
                {/* Code badge */}
                <div className="text-center">
                  <span className="inline-block bg-white/15 border border-white/20 px-3 py-1 rounded-full text-sm font-black tracking-widest">
                    {referralData.referralCode}
                  </span>
                </div>
                {/* Link */}
                <div className="bg-white/10 rounded-xl p-2.5 text-[10px] text-blue-100 font-bold break-all leading-relaxed border border-white/10">
                  {referralData.referralUrl}
                </div>
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={handleCopyReferralLink}
                    className={`flex-1 py-2 rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-1.5 ${
                      copied ? 'bg-emerald-500 text-white' : 'bg-white text-[#143E78] hover:bg-blue-50'
                    }`}
                  >
                    {copied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy Link</>}
                  </button>
                  <button
                    onClick={handleDownloadQRCode}
                    className="flex-1 py-2 rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 bg-white/10 hover:bg-white/20 text-white border border-white/20 flex items-center justify-center gap-1.5"
                  >
                    <Download className="w-3.5 h-3.5" /> Download
                  </button>
                </div>
                {/* Count */}
                <div className="flex items-center justify-between bg-white/10 rounded-xl px-3 py-2.5 border border-white/10">
                  <div>
                    <p className="text-[9px] font-black text-blue-200/70 uppercase tracking-widest">Total Referrals</p>
                    <p className="text-xl font-black text-white">{referralData.referralCount}</p>
                  </div>
                  <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-3 relative z-10">
                <p className="text-blue-200/60 text-xs font-bold">Unable to load referral data</p>
                <button onClick={fetchReferral} className="mt-1 text-xs text-white font-black underline">Retry</button>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-hidden">
          <div className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-lg shadow-2xl animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto relative">
            <button 
              onClick={() => setShowEditModal(false)}
              className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
            >
              <XCircle size={20} strokeWidth={3} />
            </button>
            <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3 pr-8">
              <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center text-primary-600 shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              Edit Users
            </h2>

            <div className="space-y-5">
              {/* Profile Image (Avatar) Picker */}
              <div className="flex flex-col items-center justify-center pb-4 border-b border-gray-100">
                <div className="relative group">
                  <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-primary-50 bg-gray-100 flex items-center justify-center relative shadow-md">
                    {formData.avatar ? (
                      <img
                        src={URL.createObjectURL(formData.avatar)}
                        className="w-full h-full object-cover"
                        alt="Avatar Preview"
                      />
                    ) : user?.avatar ? (
                      <img
                        src={getAvatarUrl(user.avatar)}
                        className="w-full h-full object-cover"
                        alt="Current Avatar"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-400">
                        <svg className="w-8 h-8 mb-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    onChange={(e) => setFormData({ ...formData, avatar: e.target.files[0] })}
                    className="hidden"
                    id="edit-avatar-upload"
                    accept="image/*"
                  />
                  <label
                    htmlFor="edit-avatar-upload"
                    className="absolute bottom-0 right-0 p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full cursor-pointer shadow-lg hover:scale-110 transition-all border-2 border-white flex items-center justify-center"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </label>
                </div>
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest mt-2">Upload Profile Photo</label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">First Name</label>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                    placeholder="John"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">Last Name</label>
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">Email Address</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                  placeholder="john.doe@doxez.com"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">Phone Number</label>
                <input
                  type="text"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  maxLength={10}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                  placeholder="10-digit number"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">Aadhaar Card</label>
                  <div className="relative">
                    <input
                      type="file"
                      onChange={(e) => setFormData({ ...formData, aadhaarCard: e.target.files[0] })}
                      className="hidden"
                      id="edit-aadhaar-upload"
                      accept=".pdf,image/*"
                    />
                    <label
                      htmlFor="edit-aadhaar-upload"
                      className={`flex flex-col items-center justify-center w-full px-4 py-3 bg-gray-50 border border-dashed rounded-xl cursor-pointer transition-all hover:bg-gray-100 ${formData.aadhaarCard || user?.aadhaarCard?.url ? 'border-emerald-200 bg-emerald-50/20' : 'border-gray-200'}`}
                    >
                      <div className="flex flex-col items-center">
                        <svg className={`w-6 h-6 mb-1 ${formData.aadhaarCard || user?.aadhaarCard?.url ? 'text-emerald-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-[9px] font-bold text-gray-500 truncate w-full text-center px-1">
                          {formData.aadhaarCard ? formData.aadhaarCard.name : user?.aadhaarCard?.url ? 'Change Aadhaar' : 'Upload Aadhaar'}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">PAN Card</label>
                  <div className="relative">
                    <input
                      type="file"
                      onChange={(e) => setFormData({ ...formData, panCard: e.target.files[0] })}
                      className="hidden"
                      id="edit-pan-upload"
                      accept=".pdf,image/*"
                    />
                    <label
                      htmlFor="edit-pan-upload"
                      className={`flex flex-col items-center justify-center w-full px-4 py-3 bg-gray-50 border border-dashed rounded-xl cursor-pointer transition-all hover:bg-gray-100 ${formData.panCard || user?.panCard?.url ? 'border-emerald-200 bg-emerald-50/20' : 'border-gray-200'}`}
                    >
                      <div className="flex flex-col items-center">
                        <svg className={`w-6 h-6 mb-1 ${formData.panCard || user?.panCard?.url ? 'text-emerald-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-[9px] font-bold text-gray-500 truncate w-full text-center px-1">
                          {formData.panCard ? formData.panCard.name : user?.panCard?.url ? 'Change PAN' : 'Upload PAN'}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Geographic Profile Section */}
              <div className="border-t border-gray-100 pt-5 space-y-4">
                <span className="text-[10px] font-black uppercase text-indigo-600 tracking-widest block px-1">Geographic Profile</span>
                
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">Street Address</label>
                  <input
                    type="text"
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                    placeholder="123 Main St"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                      placeholder="Bhubaneswar"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">State</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                      placeholder="Odisha"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">Pincode</label>
                  <input
                    type="text"
                    value={formData.pincode}
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                    placeholder="751001"
                  />
                </div>
              </div>

              {/* System Configuration Section */}
              <div className="border-t border-gray-100 pt-5 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">System Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    disabled={currentUser?.role === 'cc_head'}
                    className="w-full px-4 py-3.5 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none font-bold transition-all text-gray-700 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {availableRoles.length > 0 ? (
                      availableRoles.map((r) => (
                        <option key={r._id || r.role_name} value={r.role_name}>
                          {r.role_name === 'marketing_agent'
                            ? 'Lead Partner'
                            : r.role_name === 'cc_head'
                            ? 'Customer Care Head'
                            : r.role_name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="admin">Admin</option>
                        <option value="customer_care">Customer Care</option>
                        <option value="cc_head">Customer Care Head</option>
                        <option value="doctor">Doctor</option>
                        <option value="hospital">Hospital</option>
                        <option value="marketing_agent">Lead Partner</option>
                        <option value="accountant">Accountant</option>
                      </>
                    )}
                  </select>
                </div>

                {formData.role === 'doctor' && (
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">Doctor Type</label>
                    <select
                      value={formData.doctorCategory || ''}
                      onChange={(e) => setFormData({ ...formData, doctorCategory: e.target.value })}
                      className="w-full px-4 py-3.5 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none font-bold transition-all text-gray-700"
                    >
                      <option value="">Select Doctor Type</option>
                      <option value="inhouse">In-House Doctor (Works with your organization)</option>
                      <option value="external">External Doctor (Independent / visiting)</option>
                    </select>
                  </div>
                )}

                <div className="flex items-center gap-3 px-1 pt-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-5 w-5 text-indigo-600 focus:ring-indigo-500/10 border-gray-200 rounded-lg cursor-pointer"
                  />
                  <label htmlFor="isActive" className="text-sm font-bold text-gray-700 cursor-pointer">
                    Active User Account
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8 pt-5 border-t border-gray-100">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-6 py-3 border border-gray-200 rounded-xl text-xs font-black uppercase tracking-widest text-gray-600 hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={updating}
                className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-indigo-100 transition-all disabled:opacity-50"
              >
                {updating ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-100">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Reset User Password</h2>
            <p className="text-xs text-gray-500 mb-4">Assign a new strong password for this institutional account.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">New Password</label>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter strong new password"
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium text-sm"
                />
              </div>
              <PasswordStrengthGuide password={newPassword} />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowResetModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 font-bold text-sm text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={updating || !getPasswordValidation(newPassword).isValid}
                className="px-5 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 font-bold text-sm shadow-md transition-all"
              >
                {updating ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetail;
